import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import MarketplaceTeam from "marketplace/teams/team";
import EntityMember from "_shared/entity-members/entity-member";
import { JSONType } from "work/proposals/redlining/field-redlines/field-redline";
import ClientTeamSchema, {
  ClientTeamJSON,
  ClientTeamParams,
} from "work/work-agreements/teams/client-team/_schema/client-team-schema";

export default class ClientTeam {
  public static get EXAMPLE(): ClientTeam {
    return new ClientTeam({
      leader: EntityMember.EXAMPLE,
      members: [EntityMember.EXAMPLE, EntityMember.EXAMPLE],
    });
  }

  public static create(values: ClientTeamParams): ClientTeam {
    const parsedParams = ClientTeamSchema.validate(values);
    return new ClientTeam(parsedParams);
  }

  public static fromJSON(json: ClientTeamJSON | null | undefined): ClientTeam | null | undefined {
    if (!json) return json;

    const parsed = ClientTeamSchema.deserialize(json);
    let leader;

    if (parsed.leader) {
      leader = EntityMember.create({ ...parsed.leader });
    }

    return new ClientTeam({
      leader: leader ?? undefined,
      members: parsed.members.map((m) => {
        const member = EntityMember.create({ ...m });
        if (!member) throw new Error("Invalid member in ClientTeam JSON");
        return member;
      }),
    });
  }

  public static fromUnknownJSON(json: JSONType): ClientTeam | null | undefined {
    if (isNullOrUndefined(json)) return json;
    else if (isValidTeamJSON(json, { throw: true })) return ClientTeam.fromJSON(json);
    throw new Error("Cannot create ClientTeam from invalid JSON");
  }

  public static FromMarketplaceTeam(marketplaceTeam: MarketplaceTeam): ClientTeam | undefined {
    if (!marketplaceTeam) return undefined;
    return new ClientTeam({
      leader: marketplaceTeam.leader.toEntityMember(),
      members: marketplaceTeam.members.map((m) => m.toEntityMember()),
    });
  }

  public readonly leader?: EntityMember;

  public get members(): EntityMember[] {
    return [...this._members];
  } // Return a copy to prevent external mutation

  public constructor(params: ClientTeamParams) {
    let leader;
    if (params.leader) {
      leader = EntityMember.create({ ...params.leader });
    }
    this.leader = leader ?? undefined;
    this.members = params.members.map((member) => EntityMember.create({ ...member }));
  }

  public isEqualTo(other?: unknown): boolean {
    return (
      other instanceof ClientTeam &&
      ((!this.leader && !other.leader) || this.leader?.isEqualTo(other.leader) || false) &&
      this.members.length === other.members.length &&
      this.members.every((member) => other.members.some((otherMember) => member.isEqualTo(otherMember)))
    );
  }

  public toJSON(): ClientTeamJSON {
    return {
      leader: this.leader?.toJSON(),
      members: this._members.map((m) => m.toJSON()),
    };
  }

  public clone(): ClientTeam {
    return new ClientTeam({
      leader: this.leader ? this.leader.clone() : undefined,
      members: this._members.map((m) => m.clone()),
    });
  }

  private _members: EntityMember[] = [];

  private set members(members: EntityMember[]) {
    this._members = members;
  }
}

export class InvalidClientTeamJSON extends Error {
  public readonly json: JSONType;
  public readonly message: string = "Invalid ClientTeamJSON";

  constructor(args: { json: JSONType; message?: string }) {
    super(`${args.message}: ${JSON.stringify(args.json)}`);
    this.message = args.message ?? this.message;
    this.json = args.json;
  }
}

function isValidTeamJSON(json: JSONType, options?: { throw: boolean }): json is ClientTeamJSON {
  options = options ?? { throw: false };
  if (isNullOrUndefined(json)) return true;

  if (typeof json !== "object" || json === null) {
    if (options.throw) throw new InvalidClientTeamJSON({ json });
    return false;
  }

  if ("leader" in json && !isNullOrUndefined(json.leader)) {
    if (typeof json.leader !== "object") {
      if (options.throw)
        throw new InvalidClientTeamJSON({
          json,
          message: "Invalid ClientTeamJSON leader",
        });
      return false;
    }
  }

  if (!("members" in json) || (json.members && !Array.isArray(json.members))) {
    if (options.throw)
      throw new InvalidClientTeamJSON({
        json,
        message: "Invalid ClientTeamJSON members",
      });
    return false;
  }

  return true;
}
